import axios from 'axios';
import {
    QLIK_LOADING,
    RESET_QLIK_STATE,
    GET_QLIK_APPS,
    GET_QLIK_APP,
    GET_QLIK_SHEETS,
    GET_QLIK_OBJECTS,
    GET_QLIK_OBJECT,
    GET_QLIK_ERRORS,
    GET_QLIK_STREAMS,
    GET_QLIK_SAAS_SPACES,
    GET_ERRORS,
    SET_QLIK_OPTIONS,
    CLEAR_QLIK_OPTIONS,
} from './types';
import { resetErrors } from './errorsActions';
import { ToastsStore } from 'react-toasts';
import { saasRequest } from 'websockets/utils/SaaSUtils';
import { qlikClient } from 'constants/environmental';
import QlikSaaSService from 'websockets/service/QlikSaaSService';
import QlikWebSocketService from 'websockets/service/QlikWebSocketService';
import config from 'coeus.config';
import {getHash, makeQsOptions} from 'utils/Helper';
import { logMeIn, logoutUser } from 'redux/actions/authActions';

const SAAS = config.saas_client === qlikClient.SAAS;
const socketService = SAAS ? new QlikSaaSService() : new QlikWebSocketService();

const getNextLink = (result) => {
    return result.links.next.href.split('qlikcloud.com')[1];
};

export const getQlikSaasSpaces = () => async (dispatch, getState) => {
    dispatch(setQlikLoading());
    dispatch(resetErrors);

    try {
        const { qsServer } = getState();
        if (qsServer.qsServer) {
            const { webIntegrationId, tenantUri } = qsServer?.qsServer;
            const result = await saasRequest(
                tenantUri,
                '/api/v1/spaces?limit=100',
                webIntegrationId
            );

            dispatch({
                type: GET_QLIK_SAAS_SPACES,
                payload: result.data,
            });
        }
    } catch (err) {
        if (err.status === 401) {
            dispatch(logoutUser(getHash()));
        }

        if (err.response) {
            dispatch({ type: GET_ERRORS, payload: err?.response?.data });
        }
    }
};

export const getQlikApps =
    (space = '') =>
    async (dispatch, getState) => {
        dispatch(setQlikLoading());
        dispatch(resetErrors);

        if (SAAS) {
            try {
                const { qsServer } = getState();
                if (qsServer.qsServer) {
                    const { webIntegrationId, tenantUri } = qsServer?.qsServer;
                    const result = await saasRequest(
                        tenantUri,
                        `/api/v1/items?resourceType=app,qvapp&limit=100&shared=true${
                            space && `&spaceId=${space}`
                        }`,
                        webIntegrationId
                    );

                    let apps = result.data;

                    if (result.links.next) {
                        const nextLink = getNextLink(result);
                        const nextResult = await saasRequest(
                            tenantUri,
                            nextLink,
                            webIntegrationId
                        );
                        apps.push(nextResult.data);
                    }

                    dispatch({
                        type: GET_QLIK_APPS,
                        payload: apps,
                    });
                }
            } catch (err) {
                console.log(err);
                dispatch({ type: GET_ERRORS, payload: err });
            }
        } else {
            axios
                .get('/api/qlik/apps')
                .then((res) => {
                    dispatch({
                        type: GET_QLIK_APPS,
                        payload: res.data,
                    });
                })
                .catch((err) => {
                    dispatch({ type: GET_ERRORS, payload: err.response.data });
                });
        }
    };

export const loadQlikApps = () => (dispatch) =>
    new Promise((resolve, reject) => {
        axios
            .get('/api/qlik/apps')
            .then((res) => {
                dispatch({
                    type: GET_QLIK_APPS,
                    payload: res.data,
                });
                resolve(res.data);
            })
            .catch((err) => {
                reject(err);
            });
    });

export const getQlikApp = (app_id) => (dispatch) => {
    dispatch(setQlikLoading());
    dispatch(resetErrors);

    return axios
        .get(`/api/qlik/app/${app_id}`)
        .then((res) => {
            dispatch({
                type: GET_QLIK_APP,
                payload: res.data,
            });
        })
        .catch((err) => {
            dispatch({ type: GET_ERRORS, payload: err.response.data });
        });
};

export const getQlikStreams = () => (dispatch) => {
    dispatch(setQlikLoading());
    dispatch(resetErrors);

    return axios
        .get(`/api/qlik/streams`)
        .then((res) => {
            dispatch({
                type: GET_QLIK_STREAMS,
                payload: res.data,
            });
        })
        .catch((err) => {
            dispatch({ type: GET_QLIK_ERRORS, payload: err.response.data });
        });
};

export const getQlikSheets = (app_id) => async (dispatch, getState) => {
    dispatch(setQlikLoading());
    dispatch(resetErrors);

    if (SAAS) {
        let options = getState().qsServer.qsServer;
        options = makeQsOptions(options);

        const socket = await socketService.getOrCreateSocket({
            ...options.config,
            appId: app_id,
        });

        const sheets = await socket.getSheets();
        dispatch({
            type: GET_QLIK_SHEETS,
            payload: sheets,
        });
    } else {
        axios
            .get(`/api/qlik/app/${app_id}/sheets`)
            .then((res) => {
                dispatch({
                    type: GET_QLIK_SHEETS,
                    payload: res.data,
                });
            })
            .catch((err) => {
                if (err) {
                    if (err.response) {
                        if (err.response.data.flash) {
                            ToastsStore.error(
                                `Qlik Error ${err.response.data.flash.code} : ${err.response.data.flash.parameter}`
                            );
                        }
                        if (err.response.data) {
                            dispatch({
                                type: GET_ERRORS,
                                payload: err.response.data,
                            });
                        }
                    }
                }
            });
    }
};

export const getQlikObjects =
    (app_id, sheet_id) => async (dispatch, getState) => {
        dispatch(setQlikLoading());
        dispatch(resetErrors);

        if (SAAS) {
            let options = getState().qsServer.qsServer;
            options = makeQsOptions(options);

            const socket = await socketService.getOrCreateSocket({
                ...options,
                appId: app_id,
            });
            const sheet = await socket.getSheet(sheet_id);
            const layout = await sheet.getLayout();

            dispatch({
                type: GET_QLIK_OBJECTS,
                payload: layout.cells,
            });
        } else {
            axios
                .get(`/api/qlik/app/${app_id}/sheet/${sheet_id}/objects`)
                .then((res) => {
                    dispatch({
                        type: GET_QLIK_OBJECTS,
                        payload: res.data,
                    });
                })
                .catch((err) => {
                    dispatch({ type: GET_ERRORS, payload: err.response.data });
                });
        }
    };

export const getQlikObject = (app_id, object_id) => (dispatch) => {
    dispatch(setQlikLoading());
    dispatch(resetErrors);

    return axios
        .get(`/api/qlik/app/${app_id}/object/${object_id}`)
        .then((res) => {
            dispatch({
                type: GET_QLIK_OBJECT,
                payload: res.data,
            });
        })
        .catch((err) => {
            dispatch({ type: GET_ERRORS, payload: err.response.data });
        });
};

export const resetQlik = () => (dispatch) => {
    return { type: RESET_QLIK_STATE };
};

export const setQlikOptions = (options) => (dispatch) => {
    // dispatch(setQlikLoading())
    dispatch({ type: SET_QLIK_OPTIONS, payload: options });
};

export const clearQlikOptions = () => (dispatch) => {
    dispatch(setQlikLoading());
    dispatch({ type: CLEAR_QLIK_OPTIONS, payload: null });
};

export const setQlikLoading = () => {
    return { type: QLIK_LOADING };
};
